import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";

const scopes = [
  { num: "01", title: "Custom Homes", copy: "Ground-up custom homes built with the same scheduling discipline we bring to commercial jobs." },
  { num: "02", title: "Home Renovations", copy: "Whole-home renovations and major remodels, phased so life can keep moving around the work." },
  { num: "03", title: "Additions", copy: "Additions and expansions tied cleanly into the existing structure, finished on a committed date." },
];

export default function Residential() {
  useEffect(() => {
    document.title = "Residential Division | Rogers American Construction, Charleston SC";
  }, []);

  return (
    <main className="bg-[#F1F0ED]">
      <section className="px-6 md:px-12 pt-40 pb-20 md:pb-28" data-testid="residential-hero">
        <p className="text-xs uppercase tracking-[0.25em] text-smoke font-semibold mb-6" data-testid="residential-eyebrow">
          The Residential Division of Rogers American Construction
        </p>
        <h1 className="font-display font-medium uppercase leading-[0.92] tracking-tighter text-[11vw] md:text-[6.5vw] text-ink/80" data-testid="residential-title">
          <MaskedLine delay={0.1}>Homes, renovations,</MaskedLine>
          <MaskedLine delay={0.28}>and additions.</MaskedLine>
        </h1>
        <p className="mt-10 max-w-2xl text-smoke text-base md:text-lg leading-relaxed" data-testid="residential-intro">
          The residential division of Rogers American Construction — a family-run team
          building custom homes, renovations, and additions across the Charleston area.
        </p>
      </section>

      <section className="px-6 md:px-12 pb-20 md:pb-28" data-testid="residential-scopes">
        <div className="border-t border-ink/15">
          {scopes.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-b border-ink/15 py-10 items-start" data-testid={`residential-scope-${s.num}`}>
                <span className="md:col-span-2 font-display font-medium text-5xl md:text-6xl leading-none text-outline-dark select-none">{s.num}</span>
                <h2 className="md:col-span-4 font-display font-medium uppercase tracking-tight text-2xl text-ink/80">{s.title}</h2>
                <p className="md:col-span-6 text-smoke text-base leading-relaxed max-w-xl">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 md:pb-32" data-testid="residential-cta">
        <div className="border border-ink/20 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display font-medium uppercase tracking-tighter text-3xl md:text-4xl text-ink/80">
              Talk to the residential team
            </h2>
            <p className="mt-3 text-smoke text-base max-w-lg leading-relaxed">
              Tell us about the home project and we will route you to the right people on the residential side.
            </p>
          </div>
          <Link
            to="/request-quote?type=residential"
            data-testid="residential-quote-button"
            className="group inline-flex items-center gap-3 bg-ink text-paper font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:bg-accent transition-colors shrink-0"
          >
            Start a residential request
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </main>
  );
}
